import { Link } from "react-router-dom";
import LeadCaptureBox from "@/components/LeadCaptureBox";
import { ArrowRight, Check } from "lucide-react";

// ── Cross-sell links (shared across all feature pages) ──
const CROSS_LINKS = [
  { href: "/ai-campaign-builder", title: "AI Campaign Builder", desc: "Write emails from a single prompt" },
  { href: "/auto-optimization", title: "Auto-Optimization", desc: "AI resends to non-openers" },
  { href: "/subject-line-optimisation", title: "Subject Lines", desc: "A/B test AI-written variants" },
  { href: "/send-time-optimization", title: "Send-Time AI", desc: "Deliver at the perfect moment" },
  { href: "/engagement-scoring", title: "Engagement Scoring", desc: "Hot, warm & cold tiers" },
  { href: "/behavioral-triggers", title: "Behavioral Triggers", desc: "React to every subscriber action" },
  { href: "/revenue-attribution", title: "Revenue Attribution", desc: "Track revenue from every email" },
  { href: "/list-hygiene", title: "List Hygiene", desc: "Auto-clean inactive subscribers" },
];

export interface FeatureStat {
  value: string;
  label: string;
}

export interface FeatureBenefit {
  icon: React.ReactNode;
  title: string;
  desc: string;
}

export interface HowItWorksStep {
  title: string;
  desc: string;
}

interface Props {
  // Hero
  badge: string;
  badgeIcon: React.ReactNode;
  heroTitle: React.ReactNode;
  heroSubtitle: string;
  ctaLabel?: string;
  ctaLink?: string;

  // Stats bar (optional)
  stats?: FeatureStat[];

  // Demo (injected component, usually TypewriterDemo)
  demo?: React.ReactNode;

  // How it works (optional)
  howTitle?: string;
  howDesc?: string;
  howSteps?: HowItWorksStep[];

  // Benefits
  benefitsTitle: string;
  benefits: FeatureBenefit[];

  // Extra sections (injected between benefits and cross-sell)
  children?: React.ReactNode;

  // CTA
  ctaHeadline: string;
  ctaDescription: string;

  // Current page path (to exclude from cross-sell)
  currentPath: string;
}

export default function FeaturePageLayout({
  badge, badgeIcon, heroTitle, heroSubtitle, ctaLabel = "Start Free", ctaLink = "/register",
  stats, demo, howTitle, howDesc, howSteps,
  benefitsTitle, benefits, children,
  ctaHeadline, ctaDescription, currentPath,
}: Props) {
  const crossLinks = CROSS_LINKS.filter(l => l.href !== currentPath);

  return (
    <>
      {/* Hero */}
      <section className="pt-12 pb-8 bg-gradient-to-b from-ink to-[#162029] text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-1.5 rounded-full text-xs font-semibold mb-6 backdrop-blur-sm">
            {badgeIcon}
            {badge}
          </div>
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
            {heroTitle}
          </h1>
          <p className="text-white/50 text-base md:text-lg max-w-xl mx-auto mb-8 leading-relaxed">
            {heroSubtitle}
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to={ctaLink}
              className="inline-flex items-center gap-2 bg-coral hover:bg-coral-dark text-white px-7 py-3.5 rounded-lg text-sm font-bold transition-colors shadow-lg shadow-coral/20">
              {ctaLabel} <ArrowRight size={15} />
            </Link>
            {howSteps && (
              <a href="#how"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg text-sm font-semibold text-white/70 border border-white/20 hover:bg-white/10 transition-colors">
                How It Works
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Stats bar */}
      {stats && stats.length > 0 && (
        <section className="bg-white border-b border-sand">
          <div className="max-w-4xl mx-auto px-6 py-6">
            <div className={`grid grid-cols-${Math.min(stats.length, 4)} gap-6 text-center`}>
              {stats.map(s => (
                <div key={s.label}>
                  <p className="font-display font-bold text-ink text-2xl">{s.value}</p>
                  <p className="text-xs text-slate mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Demo */}
      {demo && (
        <section className="py-10 bg-paper">
          <div className="max-w-4xl mx-auto px-6">
            {demo}
          </div>
        </section>
      )}

      {/* How it works */}
      {howSteps && (
        <section id="how" className="py-12 bg-white">
          <div className="max-w-3xl mx-auto px-6">
            {howTitle && (
              <h2 className="font-display text-2xl font-bold text-ink text-center mb-2">{howTitle}</h2>
            )}
            {howDesc && (
              <p className="text-sm text-slate text-center mb-8 max-w-lg mx-auto">{howDesc}</p>
            )}
            <div className="space-y-4">
              {howSteps.map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-coral text-white flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-ink text-sm">{step.title}</h3>
                    <p className="text-xs text-slate leading-relaxed mt-1">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits */}
      <section className="py-12 bg-paper">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-8">{benefitsTitle}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map(b => (
              <div key={b.title} className="bg-white rounded-xl p-5 border border-sand">
                <div className="w-10 h-10 rounded-xl bg-paper flex items-center justify-center mb-3">
                  {b.icon}
                </div>
                <h3 className="font-display font-bold text-ink text-sm mb-1.5">{b.title}</h3>
                <p className="text-xs text-slate leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Extra sections */}
      {children}

      {/* Cross-sell */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-lg font-bold text-ink text-center mb-5">More AI-Powered Features</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {crossLinks.slice(0, 8).map(link => (
              <Link key={link.href} to={link.href}
                className="bg-paper rounded-xl p-3 border border-sand hover:border-coral/30 hover:shadow-sm transition-all">
                <h4 className="text-xs font-bold text-ink">{link.title}</h4>
                <p className="text-[10px] text-slate mt-0.5">{link.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-ink text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="max-w-2xl mx-auto px-6 relative z-10">
          <h2 className="font-display text-3xl font-bold text-white mb-3">{ctaHeadline}</h2>
          <p className="text-white/50 text-base mb-8 leading-relaxed">{ctaDescription}</p>
          <div className="flex justify-center mb-6"><LeadCaptureBox /></div>
          <p className="text-white/30 text-xs">No credit card required • Free plan includes 1,000 subscribers</p>
        </div>
      </section>
    </>
  );
}
